// Extracted from original/src/App.tsx:31769-33700 (`activeTab === "financials"`, ADR-08 §2.1 "copy, then cut logic"):
//   header cards (revenue, profits, expenses, net)              :31778-31841
//   period selector (day + quick picks, month, year, custom + presets, all) :31843-32086
//   tab buttons, convert button                                 :32087-32214
//   profits toolbar (capitalize, withdraw, view modes, share / PDF / print, search) :32218-32358
//   period profit banner                                        :32359-32392
//   summary: currency-sales block, by box, manual work & services, operators :32394-32635
//   daily report                                                :32636-32746
//   detailed log + liquidation list                             :32747-33094
//   expenses toolbar, banner, summary cards, salaries, by operator, daily report, detailed log :33098-33697
// Kept: every class string, icon and size, the motion frame, the card colours, the selected-period pill styles, the
// quick picks, the "$" badge on the convert button, the amber USD figures and the " ($)" suffix of USD summary
// entries, the progress bars with their percentages, the empty states, the hover-revealed trash buttons.
// Cut: `currentPeriodStats` / `transactionProfits` / `profitSummaries` / `dailyProfitsSummary` / `profitMovements` /
// `filteredExpenses` and their breakdowns (App.tsx:9784-10560, all in the browser over stored movements, D06 R-13) →
// the server's read model `GET /profits` over the one profit engine (D-098), passed in as preformatted labels;
// `new Date().toISOString()` (UTC "today") → the office dates the container passes (D-098, D-162);
// `handleDeleteMovement` / `handleDeleteExpense` → `onCancel…` (journal cancel: reason → PIN → reversal, D-091,
// D-102); `handlePrintProfits` / `handlePrintExpenses` → callbacks (the container prints the A4 report);
// `setShowCapitalizeProfit` / `setShowWithdrawProfit` / `setShowConvertUSDProfitsModal` / `setShowAddExpense` →
// callbacks opening the dialogs (FinancialsModals.tsx).
// Changed by decision: the header cards show IQD and, beside it, a USD figure when there is one (D-098: currencies
// never added; old: "3,010 د.ع", harness AC); the capitalize / withdraw buttons need `settle_profit` and the trash
// buttons `delete_movements` (D-099, D-100, D-091); the convert button follows "hide USD dealing" (D-066) and shows
// only the USD badge (D-101: USD only); the partner-settlement button (:32182-32213) waits for the partners card
// (D-127, Settings chat); a partner-charge expense row has no trash button (corrected from the journal, D-255 (e)).
// Added: data-testid attributes.
import { motion } from 'motion/react';
import {
  Activity, ArrowRight, ArrowUpRight, Briefcase, Calendar, DollarSign, FileText, History, LayoutGrid, PieChart, Plus, Printer, RefreshCw,
  Search, Share2, Trash2, TrendingDown, TrendingUp, User, Users, Wallet, X,
} from 'lucide-react';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useI18n } from '../i18n-context.js';

export type FinancialsPeriod = 'day' | 'month' | 'year' | 'custom' | 'all';
export type FinancialsTab = 'profits' | 'expenses';
export type FinancialsViewMode = 'summary' | 'logs' | 'daily';
export type FinancialsExpenseCard = 'salary' | 'tip' | 'outlet' | 'withdrawal_comm' | 'other';

/** A money figure: the IQD label (no symbol; the card adds "د.ع") and, when there is one, the USD label ("$10"). */
export interface FinancialsAmount {
  readonly iqd: string;
  readonly usd: string | null;
}

export interface FinancialsSummaryItem {
  readonly name: string;
  /** "+$10" or "+3,000 د.ع" is built here from `amount` and `usd`. */
  readonly amount: string;
  readonly usd: boolean;
}

export interface FinancialsCurrencySale {
  readonly code: string;
  readonly count: number;
  /** "17,500 د.ع" or "3.11 $" (USD-settled currency sales, D-067). */
  readonly totalLabel: string;
}

export interface FinancialsDailyRow {
  readonly date: string;
  readonly weekday: string;
  readonly count: number;
  /** "3,000" (IQD) — the cell adds "+ … د.ع" / "- … د.ع". */
  readonly totalLabel: string;
  /** Extra USD part of the day, "$10", or null. */
  readonly usdLabel: string | null;
  readonly pct: string;
}

export interface FinancialsProfitRow {
  readonly key: string;
  readonly id: string;
  readonly dateLabel: string;
  readonly timeLabel: string;
  readonly item: string;
  /** The old movement type shown in the badge ("sell", "manual_work", …). */
  readonly typeBadge: string;
  readonly isBuy: boolean;
  readonly operator: string | null;
  readonly note: string | null;
  readonly wallet: string | null;
  /** "20,000" / "-2,500" without sign handling for "+"; `negative` drives the sign. */
  readonly amountLabel: string;
  readonly negative: boolean;
  readonly usd: boolean;
  readonly cancellable: boolean;
}

export interface FinancialsLiquidationRow {
  readonly id: string;
  readonly dateLabel: string;
  readonly withdraw: boolean;
  /** "60,000 د.ع" / "$100". */
  readonly amountLabel: string;
  readonly note: string | null;
  readonly cancellable: boolean;
}

export interface FinancialsExpenseRow {
  readonly id: string;
  readonly dateLabel: string;
  readonly timeLabel: string;
  readonly category: FinancialsExpenseCard;
  readonly operator: string | null;
  readonly details: string | null;
  readonly amountLabel: string;
  readonly cancellable: boolean;
}

export interface FinancialsProps {
  readonly header: { readonly revenue: FinancialsAmount; readonly profit: FinancialsAmount; readonly expense: FinancialsAmount; readonly net: FinancialsAmount; readonly netNegative: boolean };
  // period
  readonly period: FinancialsPeriod;
  readonly onPeriodChange: (p: FinancialsPeriod) => void;
  readonly day: string;
  readonly onDayChange: (d: string) => void;
  /** Office dates for the quick picks (today, yesterday, the day before). */
  readonly quickDays: readonly [string, string, string];
  readonly month: string;
  readonly months: readonly string[];
  readonly onMonthChange: (m: string) => void;
  readonly year: string;
  readonly years: readonly string[];
  readonly onYearChange: (y: string) => void;
  readonly from: string;
  readonly to: string;
  readonly onFromChange: (d: string) => void;
  readonly onToChange: (d: string) => void;
  readonly onPreset: (days: number) => void;
  // tabs
  readonly tab: FinancialsTab;
  readonly onTabChange: (t: FinancialsTab) => void;
  readonly showConvert: boolean;
  /** "$0" — pending USD profit (D-101). */
  readonly convertBadge: string;
  readonly onConvert: () => void;
  // profits
  readonly canSettle: boolean;
  readonly canCancel: boolean;
  readonly onCapitalize: () => void;
  readonly onWithdraw: () => void;
  readonly profitViewMode: FinancialsViewMode;
  /** 'all' = old "عرض الكل": period → all data, mode → logs. */
  readonly onProfitViewMode: (m: FinancialsViewMode | 'all') => void;
  readonly onProfitShare: () => void;
  readonly onProfitPdf: () => void;
  readonly onProfitPrint: () => void;
  readonly profitSearch: string;
  readonly onProfitSearch: (s: string) => void;
  readonly profitTotal: FinancialsAmount;
  readonly showCurrencySales: boolean;
  readonly currencySales: readonly FinancialsCurrencySale[];
  readonly currencySalesTotal: string;
  readonly byBox: readonly FinancialsSummaryItem[];
  readonly byCategory: readonly FinancialsSummaryItem[];
  readonly byOperator: readonly FinancialsSummaryItem[];
  readonly profitDaily: readonly FinancialsDailyRow[];
  readonly profitLogDate: string;
  readonly onProfitLogDate: (d: string) => void;
  readonly profitRows: readonly FinancialsProfitRow[];
  readonly onCancelOperation: (id: string) => void;
  readonly liquidation: readonly FinancialsLiquidationRow[];
  // expenses
  readonly onAddExpense: () => void;
  readonly expenseViewMode: FinancialsViewMode;
  readonly onExpenseViewMode: (m: FinancialsViewMode | 'all') => void;
  readonly onExpenseShare: () => void;
  readonly onExpensePdf: () => void;
  readonly onExpensePrint: () => void;
  readonly expenseSearch: string;
  readonly onExpenseSearch: (s: string) => void;
  readonly expenseTotal: string;
  readonly expenseCount: number;
  readonly expenseCards: Readonly<Record<FinancialsExpenseCard, { readonly total: string; readonly count: number; readonly pct: string }>>;
  /** "Other" card = other + withdrawal commissions (old :33336-33361). */
  readonly expenseOther: { readonly total: string; readonly count: number; readonly pct: string };
  readonly salaries: ReadonlyArray<{ readonly name: string; readonly amount: string }>;
  readonly expenseOperators: ReadonlyArray<{ readonly name: string; readonly count: number; readonly total: string }>;
  readonly expenseDaily: readonly FinancialsDailyRow[];
  readonly expenseLogDate: string;
  readonly onExpenseLogDate: (d: string) => void;
  readonly expenseRows: readonly FinancialsExpenseRow[];
}

const PERIODS: readonly FinancialsPeriod[] = ['day', 'month', 'year', 'custom', 'all'];
const VIEW_MODES = ['summary', 'logs', 'daily', 'all'] as const;
const PRESETS: ReadonlyArray<{ key: string; days: number }> = [
  { key: 'pf.preset_1m', days: 30 },
  { key: 'pf.preset_2m', days: 60 },
  { key: 'pf.preset_1y', days: 365 },
  { key: 'pf.preset_2y', days: 730 },
  { key: 'pf.preset_5y', days: 1825 },
];

export function Financials(props: FinancialsProps) {
  const { t, locale } = useI18n();
  const lang = locale;
  const profitFilterType = props.period;
  const financialTab = props.tab;
  const profitViewMode = props.profitViewMode;
  const expenseViewMode = props.expenseViewMode;
  const selectedProfitDay = props.day;
  const [todayD, yesterdayD, dayBeforeD] = props.quickDays;
  const usdExtra = (a: FinancialsAmount) =>
    a.usd ? (
      <span className="text-sm font-black text-amber-600 dark:text-amber-400 font-mono ms-2" dir="ltr" data-testid="fin-usd-extra">
        {a.usd}
      </span>
    ) : null;
  const expenseCard = (
    key: string,
    label: string,
    countLabel: string,
    c: { total: string; count: number; pct: string },
    colors: { text: string; badge: string; bar: string },
  ) => (
    <div key={key} className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] p-4 shadow-xs">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-black ${colors.text} uppercase tracking-wider`}>{label}</span>
        <span className={`text-[10px] font-mono ${colors.badge} px-2 py-0.5 rounded-md`}>
          {c.count} {countLabel}
        </span>
      </div>
      <p className="text-xl font-black text-gray-800 dark:text-[#f0f6fc] font-mono">
        {c.total} <span className="text-xs">د.ع</span>
      </p>
      <div className="w-full bg-gray-100 dark:bg-gray-800 h-1.5 rounded-full mt-3 overflow-hidden">
        <div className={`${colors.bar} h-full rounded-full transition-all duration-500`} style={{ width: `${c.pct}%` }} />
      </div>
      <span className="text-[9px] font-bold text-gray-400 mt-1 block">
        {c.pct === '0' ? 0 : c.pct}% {t('pf.of_total')}
      </span>
    </div>
  );
  const summaryCard = (items: readonly FinancialsSummaryItem[]) => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((w, idx) => (
        <div key={idx} className="p-4 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] flex justify-between items-center">
          <span className="font-bold text-gray-600 dark:text-[#8b949e]">{w.name}</span>
          <span className={`font-black font-mono ${w.usd ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
            +{w.usd ? `$${w.amount}` : `${w.amount} د.ع`}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <motion.div
      key="financials"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white dark:bg-[#0d1117] rounded-2xl shadow-sm border border-gray-100 dark:border-[#30363d] p-6 dark:digital-border"
      data-testid="financials-screen"
    >
      {/* Dynamic Period Stats Header Card Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* 1. Total Revenues Card */}
        <div className="bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-2xl border border-blue-100/50 dark:border-blue-900/20 shadow-xs" data-testid="fin-card-revenue">
          <p className="text-blue-500 dark:text-blue-400 text-[10px] font-black uppercase tracking-wider mb-1.5">{t('pf.card_revenue')}</p>
          <p className="text-2xl font-black text-blue-700 dark:text-blue-400 font-mono dark:digital-glow leading-none">
            {props.header.revenue.iqd} <span className="text-[10px] font-bold">د.ع</span>
            {usdExtra(props.header.revenue)}
          </p>
        </div>
        {/* 2. Total Profits Card */}
        <div className="bg-emerald-50/50 dark:bg-emerald-950/10 p-5 rounded-2xl border border-emerald-100/50 dark:border-emerald-950/20 shadow-xs" data-testid="fin-card-profit">
          <p className="text-emerald-500 dark:text-emerald-400 text-[10px] font-black uppercase tracking-wider mb-1.5">{t('pf.card_profit')}</p>
          <p className="text-2xl font-black text-emerald-700 dark:text-emerald-400 font-mono dark:digital-glow leading-none">
            {props.header.profit.iqd} <span className="text-[10px] font-bold">د.ع</span>
            {usdExtra(props.header.profit)}
          </p>
        </div>
        {/* 3. Total Expenses Card */}
        <div className="bg-rose-50/50 dark:bg-rose-900/10 p-5 rounded-2xl border border-rose-100/50 dark:border-rose-900/20 shadow-xs" data-testid="fin-card-expense">
          <p className="text-rose-500 dark:text-rose-400 text-[10px] font-black uppercase tracking-wider mb-1.5">{t('pf.card_expense')}</p>
          <p className="text-2xl font-black text-rose-700 dark:text-rose-400 font-mono dark:digital-glow leading-none">
            {props.header.expense.iqd} <span className="text-[10px] font-bold">د.ع</span>
            {usdExtra(props.header.expense)}
          </p>
        </div>
        {/* 4. Net Profit Card */}
        <div
          className={`p-5 rounded-2xl border shadow-xs ${
            !props.header.netNegative
              ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-100/50 dark:border-amber-900/20'
              : 'bg-red-50/50 dark:bg-red-900/10 border-red-100/50 dark:border-red-900/20'
          }`}
          data-testid="fin-card-net"
        >
          <p className={`text-[10px] font-black uppercase tracking-wider mb-1.5 ${!props.header.netNegative ? 'text-amber-500 dark:text-amber-400' : 'text-red-500'}`}>
            {t('pf.card_net')}
          </p>
          <p className={`text-2xl font-black font-mono dark:digital-glow leading-none ${!props.header.netNegative ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
            {props.header.net.iqd} <span className="text-[10px] font-bold">د.ع</span>
            {usdExtra(props.header.net)}
          </p>
        </div>
      </div>
      {/* Unified Date & Period Selector */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-[#05070a] rounded-2xl border border-gray-100 dark:border-[#30363d] flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div>
          <span className="text-[10px] font-black text-gray-400 dark:text-[#8b949e] uppercase tracking-wider mb-2 block font-sans">{t('pf.scope')}</span>
          <div className="flex bg-white dark:bg-[#0d1117] rounded-xl p-1 border border-gray-100 dark:border-[#30363d] w-fit shadow-xs flex-wrap gap-1">
            {PERIODS.map((type) => (
              <button
                key={type}
                onClick={() => props.onPeriodChange(type)}
                className={`px-4 py-1.5 text-[11px] font-black rounded-lg transition-all ${
                  profitFilterType === type ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-700 dark:hover:text-[#f0f6fc]'
                }`}
                data-testid={`fin-period-${type}`}
              >
                {t(`pf.period_${type}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 max-w-md">
          <span className="text-[10px] font-black text-gray-400 dark:text-[#8b949e] uppercase tracking-wider mb-2 block font-sans">{t('pf.selected_period')}</span>
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-400" />
            {profitFilterType === 'day' && (
              <div className="flex-1 flex flex-col gap-2">
                <EnglishDatePicker value={selectedProfitDay} onChange={(val) => props.onDayChange(val)} className="w-full !py-2 !text-sm" />
                {/* Quick Picks for days */}
                <div className="flex flex-wrap gap-1.5">
                  {([
                    [todayD, 'pf.today'],
                    [yesterdayD, 'pf.yesterday'],
                    [dayBeforeD, 'pf.day_before'],
                  ] as const).map(([d, k]) => (
                    <button
                      key={k}
                      onClick={() => props.onDayChange(d)}
                      className={`px-2 py-1 text-[10px] font-bold rounded-lg transition-all ${
                        selectedProfitDay === d
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-extrabold'
                          : 'bg-white dark:bg-[#0d1117] text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-100 dark:border-[#30363d]'
                      }`}
                    >
                      {t(k)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {profitFilterType === 'month' && (
              <select
                value={props.month}
                onChange={(e) => props.onMonthChange(e.target.value)}
                className="flex-1 text-sm font-black bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-xl px-3 py-2 focus:outline-none dark:text-[#f0f6fc] shadow-xs"
              >
                {props.months.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            )}
            {profitFilterType === 'year' && (
              <select
                value={props.year}
                onChange={(e) => props.onYearChange(e.target.value)}
                className="flex-1 text-sm font-black bg-white dark:bg-[#0d1117] border border-gray-100 dark:border-[#30363d] rounded-xl px-3 py-2 focus:outline-none dark:text-[#f0f6fc] shadow-xs"
              >
                {props.years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            )}
            {profitFilterType === 'custom' && (
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <EnglishDatePicker value={props.from} onChange={(val) => props.onFromChange(val)} className="flex-1 !py-1.5 !text-xs text-center" />
                  <span className="text-[10px] font-bold text-gray-400">{t('pf.to')}</span>
                  <EnglishDatePicker value={props.to} onChange={(val) => props.onToChange(val)} className="flex-1 !py-1.5 !text-xs text-center" />
                </div>
                {/* Quick Picks for years/months ago */}
                <div className="flex flex-wrap gap-1">
                  {PRESETS.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => props.onPreset(item.days)}
                      className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-950/80 transition-colors border border-blue-100/50 dark:border-blue-900/30"
                    >
                      {t(item.key)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {profitFilterType === 'all' && (
              <div className="flex-1 text-xs font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-950/50 rounded-xl px-4 py-2.5 font-sans">
                {t('pf.all_note')}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 mb-8">
        <div className="bg-gray-100/60 dark:bg-[#161b22]/50 p-1 rounded-2xl border border-gray-200/50 dark:border-[#30363d] flex gap-1 w-full max-w-md">
          <button
            onClick={() => props.onTabChange('profits')}
            className={`flex-1 py-3 px-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-tight ${
              financialTab === 'profits' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#f0f6fc]'
            }`}
            data-testid="fin-tab-profits"
          >
            <TrendingUp size={16} />
            <span>{t('pf.tab_profits')}</span>
          </button>
          <button
            onClick={() => props.onTabChange('expenses')}
            className={`flex-1 py-3 px-4 rounded-xl font-black transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-tight ${
              financialTab === 'expenses' ? 'bg-rose-600 text-white shadow-md' : 'text-gray-500 dark:text-[#8b949e] hover:text-gray-900 dark:hover:text-[#f0f6fc]'
            }`}
            data-testid="fin-tab-expenses"
          >
            <TrendingDown size={16} />
            <span>{t('pf.tab_expenses')}</span>
          </button>
        </div>
        {props.showConvert && (
          <button
            onClick={props.onConvert}
            className="py-3 px-4 rounded-2xl font-black transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-tight bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-500/20 hover:from-amber-600 hover:to-amber-700 active:scale-95"
            title={t('pf.convert_title')}
            data-testid="fin-convert"
          >
            <RefreshCw size={16} />
            <span>{t('pf.convert')}</span>
            <div className="flex items-center gap-1">
              <span className="bg-white/20 px-2 py-0.5 rounded-lg text-[10px] font-mono">{props.convertBadge}</span>
            </div>
          </button>
        )}
      </div>
      {financialTab === 'profits' ? (
        <div className="space-y-6">
          {/* Refined & Streamlined Action & Toolbar Bar */}
          <div className="flex flex-col xl:flex-row justify-between items-stretch xl:items-center gap-4 bg-white/70 dark:bg-[#0d1117]/70 p-3.5 rounded-2xl border border-gray-200/70 dark:border-[#30363d] shadow-2xs">
            {/* Actions, View Modes, & Print/Share Capsule */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Capitalize & Withdraw Buttons */}
              {props.canSettle && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={props.onCapitalize}
                    className="px-3.5 py-2 rounded-xl font-bold transition-all uppercase tracking-tight bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs flex items-center gap-1.5 text-xs active:scale-95 cursor-pointer"
                    title={t('pf.capitalize_title')}
                    data-testid="fin-capitalize"
                  >
                    <TrendingUp size={16} />
                    <span>{t('pf.capitalize')}</span>
                  </button>
                  <button
                    onClick={props.onWithdraw}
                    className="px-3.5 py-2 rounded-xl font-bold transition-all uppercase tracking-tight bg-blue-600 hover:bg-blue-700 text-white shadow-xs flex items-center gap-1.5 text-xs active:scale-95 cursor-pointer"
                    title={t('pf.withdraw_title')}
                    data-testid="fin-withdraw"
                  >
                    <ArrowUpRight size={16} />
                    <span>{t('withdrawProfit')}</span>
                  </button>
                </div>
              )}
              <div className="hidden sm:block h-6 w-[1px] bg-gray-200 dark:bg-[#30363d] mx-0.5"></div>
              {/* View Mode Tabs */}
              <div className="flex bg-gray-100/80 dark:bg-[#05070a] rounded-xl p-1 border border-gray-200/80 dark:border-[#30363d] overflow-x-auto">
                {VIEW_MODES.map((mode) => (
                  <button
                    key={mode}
                    onClick={() => props.onProfitViewMode(mode)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap uppercase tracking-tight ${
                      (profitViewMode === mode && (mode !== 'logs' || profitFilterType !== 'all')) || (mode === 'all' && profitFilterType === 'all')
                        ? 'bg-white dark:bg-[#161b22] text-blue-600 dark:text-blue-400 shadow-xs border border-gray-200/80 dark:border-[#30363d]'
                        : 'text-gray-500 hover:text-gray-900 dark:hover:text-[#f0f6fc]'
                    }`}
                    data-testid={`fin-pmode-${mode}`}
                  >
                    {t(`pf.mode_${mode}`)}
                  </button>
                ))}
              </div>
              <div className="hidden sm:block h-6 w-[1px] bg-gray-200 dark:bg-[#30363d] mx-0.5"></div>
              {/* Action Capsule: Share, PDF, Print */}
              <div className="flex items-center gap-4 sm:gap-6 px-4 py-1.5 bg-emerald-50/80 dark:bg-[#161b22] border border-emerald-200/80 dark:border-[#30363d] rounded-2xl sm:rounded-full shadow-2xs">
                <button
                  type="button"
                  onClick={props.onProfitShare}
                  title={t('pf.share_whatsapp')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                >
                  <Share2 size={19} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onProfitPdf}
                  title={t('pf.export_pdf')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                >
                  <FileText size={19} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onProfitPrint}
                  title={t('pf.print_statement')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                  data-testid="fin-profit-print"
                >
                  <Printer size={19} className="stroke-[1.8]" />
                </button>
              </div>
            </div>
            {/* Search Field */}
            <div className="relative w-full xl:w-64">
              <Search className={`absolute ${lang === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#8b949e]`} size={17} />
              <input
                type="text"
                placeholder={t('search')}
                value={props.profitSearch}
                onChange={(e) => props.onProfitSearch(e.target.value)}
                className={`w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === 'ar' ? 'pr-9 pl-9' : 'pl-9 pr-9'} py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] transition-all`}
                data-testid="fin-profit-search"
              />
              {props.profitSearch && (
                <button
                  onClick={() => props.onProfitSearch('')}
                  className={`absolute ${lang === 'ar' ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all`}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-900/10 rounded-2xl border border-emerald-100 dark:border-emerald-900/30 p-5 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500 text-white rounded-2xl shadow-lg shadow-emerald-500/20">
                <TrendingUp size={24} />
              </div>
              <div>
                <span className="text-[10px] font-black text-emerald-600/60 dark:text-emerald-400/60 uppercase tracking-[0.2em] block mb-1">{t('pf.period_total')}</span>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-2xl font-black text-emerald-700 dark:text-emerald-400 font-mono tracking-tight" data-testid="fin-profit-total">
                    {props.profitTotal.iqd} <span className="text-sm">د.ع</span>
                  </span>
                  {props.profitTotal.usd && (
                    <span className="text-xl font-black text-amber-600 dark:text-amber-400 font-mono tracking-tight bg-amber-100/60 dark:bg-amber-950/40 px-3 py-1 rounded-xl border border-amber-200 dark:border-amber-900/40">
                      + {props.profitTotal.usd}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {profitViewMode === 'daily' && (
              <div className="bg-white/50 dark:bg-emerald-900/20 px-4 py-2 rounded-xl border border-emerald-100/50 dark:border-emerald-800/30">
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-500 uppercase tracking-wider">{t('pf.spread_days', { n: props.profitDaily.length })}</span>
              </div>
            )}
          </div>
          <div className="space-y-8">
            {profitViewMode === 'summary' ? (
              <div className="space-y-6">
                {/* Currency Vault Sales Profits Summary Block - Super Manager Only */}
                {props.showCurrencySales && (
                  <div className="bg-gradient-to-br from-indigo-900/5 via-emerald-900/5 to-slate-900/5 dark:from-indigo-950/30 dark:to-emerald-950/30 rounded-2xl border border-indigo-200/60 dark:border-indigo-900/40 p-6 shadow-xs">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <DollarSign size={18} className="text-emerald-500" />
                        <h4 className="text-sm font-black text-gray-800 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.currency_sales_title')}</h4>
                      </div>
                      <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/40 px-3 py-1 rounded-xl font-mono">
                        {t('pf.currency_sales_badge')}
                      </span>
                    </div>
                    {props.currencySales.length === 0 ? (
                      <p className="text-xs text-gray-400 font-bold py-2">{t('pf.currency_sales_empty')}</p>
                    ) : (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {props.currencySales.map((item, idx) => (
                            <div key={idx} className="p-4 bg-white dark:bg-[#0d1117] rounded-xl border border-gray-200 dark:border-[#30363d] flex justify-between items-center shadow-2xs">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 text-[10px] font-black flex items-center justify-center font-mono">
                                  #{idx + 1}
                                </span>
                                <div>
                                  <span className="font-black text-xs text-gray-800 dark:text-[#f0f6fc] block">{item.code}</span>
                                  <span className="text-[10px] font-bold text-gray-400">
                                    {item.count} {t('pf.sales_count')}
                                  </span>
                                </div>
                              </div>
                              <span className="font-black font-mono text-emerald-600 dark:text-emerald-400 text-sm">+{item.totalLabel}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-end items-center pt-2 border-t border-gray-100 dark:border-[#30363d]">
                          <span className="text-xs font-black text-gray-500 font-mono">
                            {t('pf.currency_sales_total')}
                            <span className="text-emerald-600 dark:text-emerald-400">+{props.currencySalesTotal}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Wallets/Boxes Profits */}
                <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Wallet size={16} className="text-blue-500" />
                    <h4 className="text-sm font-black text-gray-700 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.by_box')}</h4>
                  </div>
                  {summaryCard(props.byBox)}
                </div>
                {/* Categories/Manual Work */}
                <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Briefcase size={16} className="text-purple-500" />
                    <h4 className="text-sm font-black text-gray-700 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.by_category')}</h4>
                  </div>
                  {summaryCard(props.byCategory)}
                </div>
                {/* Operator Performance */}
                <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={16} className="text-rose-500" />
                    <h4 className="text-sm font-black text-gray-700 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.by_operator')}</h4>
                  </div>
                  {summaryCard(props.byOperator)}
                </div>
              </div>
            ) : profitViewMode === 'daily' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-2">
                  <LayoutGrid size={16} className="text-blue-500" />
                  <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('pf.daily_title')}</h4>
                </div>
                <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] overflow-hidden">
                  <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`}>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-[#161b22] text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('date')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.ops_count')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.day_profit')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.pct_total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.profitDaily.map((day) => (
                        <tr key={day.date} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/10 flex items-center justify-center text-blue-600 font-black text-[10px]">{day.date.split('-')[2]}</div>
                              <div>
                                <p className="text-sm font-black dark:text-[#f0f6fc] font-mono">{day.date}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{day.weekday}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-[#161b22] text-xs font-black text-gray-600 dark:text-[#8b949e]">
                              {day.count} {t('pf.ops_unit')}
                            </span>
                          </td>
                          <td className="p-4 text-emerald-600 dark:text-emerald-400 font-black font-mono">
                            +{day.totalLabel} د.ع
                            {day.usdLabel && <span className="text-amber-600 dark:text-amber-400 ms-2">+ {day.usdLabel}</span>}
                          </td>
                          <td className="p-4">
                            <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${day.pct}%` }}></div>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 mt-1 block">{day.pct === '0' ? 0 : day.pct}%</span>
                          </td>
                        </tr>
                      ))}
                      {props.profitDaily.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-12 text-center text-gray-400 font-bold">
                            {t('pf.no_summary')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Transaction Profit Log - Detailed View */}
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                    <div className="flex items-center gap-2">
                      <History size={16} className="text-emerald-500" />
                      <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('pf.log_title')}</h4>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <EnglishDatePicker value={props.profitLogDate} onChange={(val) => props.onProfitLogDate(val)} className="!py-1.5 !text-xs" />
                        {props.profitLogDate && (
                          <button
                            onClick={() => props.onProfitLogDate('')}
                            className={`absolute ${lang === 'ar' ? 'left-2.5' : 'right-2.5'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all z-10`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-blue-500 bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1.5 rounded-xl">
                      {props.profitRows.length} {t('pf.profit_entries')}
                    </span>
                  </div>
                  <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`}>
                        <thead>
                          <tr className="bg-gray-50/50 dark:bg-[#161b22]/50 text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('date')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.details')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px] hidden lg:table-cell">{t('pf.wallet')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.net_profit')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px] w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {props.profitRows.map((m) => (
                            <tr key={m.key} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/30 dark:hover:bg-[#161b22]/30 transition-all group" data-testid="fin-profit-row">
                              <td className="p-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-black text-gray-700 dark:text-[#f0f6fc] font-mono">{m.dateLabel}</span>
                                  <span className="text-[9px] font-bold text-gray-400 font-mono">{m.timeLabel}</span>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${m.isBuy ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'}`}>
                                    {m.isBuy ? <TrendingUp size={14} /> : <ArrowRight size={14} />}
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className="text-sm font-black text-gray-800 dark:text-[#f0f6fc]">{m.item}</span>
                                      <span
                                        className={`text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-tighter ${m.isBuy ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}
                                      >
                                        {m.typeBadge}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold">
                                      <span className="flex items-center gap-1">
                                        <User size={10} /> {m.operator || t('pf.system')}
                                      </span>
                                      {m.note && <span className="opacity-40">|</span>}
                                      {m.note && <span className="truncate max-w-[150px]">{m.note}</span>}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4 hidden lg:table-cell">
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                  <span className="text-xs font-bold text-gray-500">{m.wallet || t('pf.general')}</span>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex flex-col items-end">
                                  <span className={`text-lg font-black font-mono ${m.usd ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                    {m.negative ? '' : '+'}
                                    {m.usd ? `$${m.amountLabel}` : m.amountLabel}
                                  </span>
                                  <span className="text-[9px] font-black text-gray-400 opacity-60">{m.usd ? 'USD ($)' : 'د.ع'}</span>
                                </div>
                              </td>
                              <td className="p-4 text-center">
                                {props.canCancel && m.cancellable && (
                                  <button
                                    onClick={() => props.onCancelOperation(m.id)}
                                    className="p-2 text-gray-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                    data-testid="fin-profit-cancel"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {props.profitRows.length === 0 && (
                      <div className="p-20 text-center flex flex-col items-center justify-center">
                        <div className="p-5 bg-gray-50 dark:bg-[#161b22] rounded-full mb-4">
                          <PieChart size={40} className="text-gray-200 dark:text-gray-700" />
                        </div>
                        <p className="text-gray-400 font-black text-sm uppercase tracking-[0.2em]">{t('pf.no_profit_data')}</p>
                        <p className="text-gray-300 dark:text-gray-600 text-[10px] mt-1">{t('pf.no_profit_hint')}</p>
                      </div>
                    )}
                  </div>
                </div>
                {/* Profit Movements (Capitalization/Withdrawal) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 px-2">
                    <Activity size={16} className="text-purple-500" />
                    <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('pf.liquidation_title')}</h4>
                  </div>
                  <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] overflow-hidden">
                    <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`}>
                      <thead>
                        <tr className="bg-gray-50 dark:bg-[#161b22] text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                          <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('date')}</th>
                          <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('type')}</th>
                          <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('amount')}</th>
                          <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('note')}</th>
                          <th className="p-4 font-bold uppercase tracking-widest text-[10px] w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {props.liquidation.map((m) => (
                          <tr key={m.id} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group" data-testid="fin-liquidation-row">
                            <td className="py-4 px-4 text-gray-400 dark:text-[#8b949e] text-xs font-bold font-mono">{m.dateLabel}</td>
                            <td className="py-4 px-4">
                              <span
                                className={`px-2 py-1 rounded-md text-[10px] font-black uppercase ${m.withdraw ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600'}`}
                              >
                                {m.withdraw ? t('pf.liq_withdraw') : t('pf.liq_capitalize')}
                              </span>
                            </td>
                            <td className="py-4 px-4 font-black font-mono dark:text-[#f0f6fc]">{m.amountLabel}</td>
                            <td className="py-4 px-4 text-xs text-gray-500 dark:text-[#8b949e] truncate max-w-[200px]">{m.note || '-'}</td>
                            <td className="py-4 px-4 text-center">
                              {props.canCancel && m.cancellable && (
                                <button
                                  onClick={() => props.onCancelOperation(m.id)}
                                  className="p-2 text-gray-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                  data-testid="fin-liquidation-cancel"
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {props.liquidation.length === 0 && (
                          <tr>
                            <td colSpan={4} className="p-8 text-center text-gray-400 font-bold">
                              {t('pf.no_liquidation')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-col xl:flex-row justify-between items-stretch xl:items-center gap-4 bg-white/70 dark:bg-[#0d1117]/70 p-3.5 rounded-2xl border border-gray-200/70 dark:border-[#30363d] shadow-2xs">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={props.onAddExpense}
                  className="px-3.5 py-2 rounded-xl font-bold transition-all uppercase tracking-tight bg-rose-600 hover:bg-rose-700 text-white shadow-xs flex items-center gap-1.5 text-xs active:scale-95 cursor-pointer"
                  title={t('pf.add_expense_title')}
                  data-testid="fin-add-expense"
                >
                  <Plus size={16} />
                  <span>{t('addExpense')}</span>
                </button>
              </div>
              <div className="hidden sm:block h-6 w-[1px] bg-gray-200 dark:bg-[#30363d] mx-0.5"></div>
              <div className="flex bg-gray-100/80 dark:bg-[#05070a] rounded-xl p-1 border border-gray-200/80 dark:border-[#30363d] overflow-x-auto">
                {VIEW_MODES.map((mode) => (
                  <button
                    key={mode}
                    onClick={() => props.onExpenseViewMode(mode)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all whitespace-nowrap uppercase tracking-tight ${
                      (expenseViewMode === mode && (mode !== 'logs' || profitFilterType !== 'all')) || (mode === 'all' && profitFilterType === 'all')
                        ? 'bg-white dark:bg-[#161b22] text-rose-600 dark:text-rose-400 shadow-xs border border-gray-200/80 dark:border-[#30363d]'
                        : 'text-gray-500 hover:text-gray-900 dark:hover:text-[#f0f6fc]'
                    }`}
                    data-testid={`fin-emode-${mode}`}
                  >
                    {t(`pf.mode_${mode}`)}
                  </button>
                ))}
              </div>
              <div className="hidden sm:block h-6 w-[1px] bg-gray-200 dark:bg-[#30363d] mx-0.5"></div>
              <div className="flex items-center gap-4 sm:gap-6 px-4 py-1.5 bg-emerald-50/80 dark:bg-[#161b22] border border-emerald-200/80 dark:border-[#30363d] rounded-2xl sm:rounded-full shadow-2xs">
                <button
                  type="button"
                  onClick={props.onExpenseShare}
                  title={t('pf.share_whatsapp')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                >
                  <Share2 size={19} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onExpensePdf}
                  title={t('pf.export_expenses_pdf')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                >
                  <FileText size={19} className="stroke-[1.8]" />
                </button>
                <button
                  type="button"
                  onClick={props.onExpensePrint}
                  title={t('pf.print_expenses')}
                  className="p-1.5 text-gray-700 dark:text-[#f0f6fc] hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-emerald-100/60 dark:hover:bg-emerald-900/40 rounded-xl transition-all cursor-pointer active:scale-90"
                  data-testid="fin-expense-print"
                >
                  <Printer size={19} className="stroke-[1.8]" />
                </button>
              </div>
            </div>
            <div className="relative w-full xl:w-64">
              <Search className={`absolute ${lang === 'ar' ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-[#8b949e]`} size={17} />
              <input
                type="text"
                placeholder={t('search')}
                value={props.expenseSearch}
                onChange={(e) => props.onExpenseSearch(e.target.value)}
                className={`w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl ${lang === 'ar' ? 'pr-9 pl-9' : 'pl-9 pr-9'} py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-rose-500/20 dark:text-[#f0f6fc] transition-all`}
                data-testid="fin-expense-search"
              />
              {props.expenseSearch && (
                <button
                  onClick={() => props.onExpenseSearch('')}
                  className={`absolute ${lang === 'ar' ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all`}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          </div>
          <div className="bg-rose-50 dark:bg-rose-900/10 rounded-2xl border border-rose-100 dark:border-rose-900/30 p-5 flex justify-between items-center shadow-sm">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-500 text-white rounded-2xl shadow-lg shadow-rose-500/20">
                <TrendingDown size={24} />
              </div>
              <div>
                <span className="text-[10px] font-black text-rose-600/60 dark:text-rose-400/60 uppercase tracking-[0.2em] block mb-1">{t('pf.expense_period_total')}</span>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-2xl font-black text-rose-700 dark:text-rose-400 font-mono tracking-tight" data-testid="fin-expense-total">
                    {props.expenseTotal} <span className="text-sm">د.ع</span>
                  </span>
                  <span className="text-xs font-bold text-gray-500 dark:text-[#8b949e] bg-white/60 dark:bg-black/20 px-2.5 py-1 rounded-xl border border-rose-100 dark:border-rose-900/40 font-mono">
                    {props.expenseCount} {t('pf.expense_entries')}
                  </span>
                </div>
              </div>
            </div>
            {expenseViewMode === 'daily' && (
              <div className="bg-white/50 dark:bg-rose-900/20 px-4 py-2 rounded-xl border border-rose-100/50 dark:border-rose-800/30">
                <span className="text-[10px] font-bold text-rose-600 dark:text-rose-500 uppercase tracking-wider">{t('pf.spread_days', { n: props.expenseDaily.length })}</span>
              </div>
            )}
          </div>
          <div className="space-y-8">
            {expenseViewMode === 'summary' ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {expenseCard('salary', t('salary'), t('pf.pays'), props.expenseCards.salary, { text: 'text-blue-600 dark:text-blue-400', badge: 'bg-blue-50 dark:bg-blue-950/40 text-blue-600', bar: 'bg-blue-500' })}
                  {expenseCard('tip', t('tip'), t('pf.items'), props.expenseCards.tip, { text: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-50 dark:bg-amber-950/40 text-amber-600', bar: 'bg-amber-500' })}
                  {expenseCard('outlet', t('outlet'), t('pf.items'), props.expenseCards.outlet, { text: 'text-purple-600 dark:text-purple-400', badge: 'bg-purple-50 dark:bg-purple-950/40 text-purple-600', bar: 'bg-purple-500' })}
                  {expenseCard('other', t('other'), t('pf.items'), props.expenseOther, { text: 'text-rose-600 dark:text-rose-400', badge: 'bg-rose-50 dark:bg-rose-950/40 text-rose-600', bar: 'bg-rose-500' })}
                </div>

                {props.salaries.length > 0 && (
                  <div className="bg-gradient-to-br from-blue-900/5 via-indigo-900/5 to-slate-900/5 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-2xl border border-blue-200/60 dark:border-blue-900/40 p-6 shadow-xs">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <User size={18} className="text-blue-600 dark:text-blue-400" />
                        <h4 className="text-sm font-black text-gray-800 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.salary_breakdown')}</h4>
                      </div>
                      <span className="text-[10px] font-black text-blue-600 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/40 px-3 py-1 rounded-xl font-mono">
                        {props.salaries.length} {t('pf.employees')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {props.salaries.map((s, idx) => (
                        <div key={idx} className="bg-white dark:bg-[#0d1117] rounded-xl p-3.5 border border-blue-100 dark:border-[#30363d] flex justify-between items-center shadow-2xs">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center font-bold text-xs">{s.name.charAt(0)}</div>
                            <span className="text-xs font-bold text-gray-700 dark:text-[#f0f6fc]">{s.name}</span>
                          </div>
                          <span className="text-sm font-black text-blue-600 dark:text-blue-400 font-mono">{s.amount} د.ع</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {props.expenseOperators.length > 0 && (
                  <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] p-6 shadow-xs">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingDown size={18} className="text-rose-500" />
                      <h4 className="text-sm font-black text-gray-800 dark:text-[#f0f6fc] uppercase tracking-widest">{t('pf.expense_by_operator')}</h4>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {props.expenseOperators.map((op, idx) => (
                        <div key={idx} className="bg-gray-50 dark:bg-[#161b22] rounded-xl p-3 border border-gray-100 dark:border-[#30363d] flex justify-between items-center">
                          <div>
                            <p className="text-xs font-bold text-gray-700 dark:text-[#f0f6fc]">{op.name}</p>
                            <span className="text-[10px] text-gray-400">
                              {op.count} {t('pf.movements_unit')}
                            </span>
                          </div>
                          <span className="text-sm font-black text-rose-600 dark:text-rose-400 font-mono">-{op.total} د.ع</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : expenseViewMode === 'daily' ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 px-2">
                  <LayoutGrid size={16} className="text-rose-500" />
                  <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('pf.expense_daily_title')}</h4>
                </div>
                <div className="bg-white dark:bg-[#0d1117] rounded-2xl border border-gray-100 dark:border-[#30363d] overflow-hidden">
                  <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`}>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-[#161b22] text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('date')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.expenses_count')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.day_expenses')}</th>
                        <th className="p-4 font-bold uppercase tracking-widest text-[10px]">{t('pf.pct_total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {props.expenseDaily.map((day) => (
                        <tr key={day.date} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/50 dark:hover:bg-[#161b22] transition-colors group">
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-rose-50 dark:bg-rose-900/10 flex items-center justify-center text-rose-600 font-black text-[10px]">{day.date.split('-')[2]}</div>
                              <div>
                                <p className="text-sm font-black dark:text-[#f0f6fc] font-mono">{day.date}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{day.weekday}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-[#161b22] text-xs font-black text-gray-600 dark:text-[#8b949e]">
                              {day.count} {t('pf.items_unit')}
                            </span>
                          </td>
                          <td className="p-4 text-rose-600 dark:text-rose-400 font-black font-mono">-{day.totalLabel} د.ع</td>
                          <td className="p-4">
                            <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                              <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${day.pct}%` }}></div>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 mt-1 block">{day.pct === '0' ? 0 : day.pct}%</span>
                          </td>
                        </tr>
                      ))}
                      {props.expenseDaily.length === 0 && (
                        <tr>
                          <td colSpan={4} className="p-12 text-center text-gray-400 font-bold">
                            {t('pf.no_summary')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                    <div className="flex items-center gap-2">
                      <History size={16} className="text-rose-500" />
                      <h4 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('pf.expense_log_title')}</h4>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <EnglishDatePicker value={props.expenseLogDate} onChange={(val) => props.onExpenseLogDate(val)} className="!py-1.5 !text-xs" />
                        {props.expenseLogDate && (
                          <button
                            onClick={() => props.onExpenseLogDate('')}
                            className={`absolute ${lang === 'ar' ? 'left-2.5' : 'right-2.5'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-rose-500 transition-all z-10`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                      <span className="text-[10px] font-black text-rose-500 bg-rose-50 dark:bg-rose-900/20 px-2.5 py-1.5 rounded-xl font-mono">
                        {props.expenseCount} {t('pf.expense_entries')}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} border-collapse`}>
                        <thead>
                          <tr className="bg-gray-50/50 dark:bg-[#161b22]/50 text-gray-400 dark:text-[#8b949e] border-b border-gray-100 dark:border-[#30363d]">
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('date')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.expense_category')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.recorded_by')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.details')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px]">{t('pf.amount')}</th>
                            <th className="p-4 font-black uppercase tracking-tighter text-[10px] w-12"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {props.expenseRows.map((e) => (
                            <tr key={e.id} className="border-b border-gray-50 dark:border-[#30363d] hover:bg-gray-50/30 dark:hover:bg-[#161b22]/30 transition-all group" data-testid="fin-expense-row">
                              <td className="p-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-black text-gray-700 dark:text-[#f0f6fc] font-mono">{e.dateLabel}</span>
                                  <span className="text-[9px] font-bold text-gray-400 font-mono">{e.timeLabel}</span>
                                </div>
                              </td>
                              <td className="p-4">
                                <span
                                  className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${
                                    e.category === 'salary'
                                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200/50 dark:border-blue-900/30'
                                      : e.category === 'tip'
                                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-900/30'
                                        : e.category === 'outlet'
                                          ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border border-purple-200/50 dark:border-purple-900/30'
                                          : e.category === 'withdrawal_comm'
                                            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-900/30'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200/50 dark:border-gray-700'
                                  }`}
                                >
                                  {t(e.category)}
                                </span>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-1 text-xs font-bold text-gray-700 dark:text-[#f0f6fc]">
                                  <User size={12} className="text-gray-400" />
                                  <span>{e.operator || t('pf.unknown')}</span>
                                </div>
                              </td>
                              <td className="p-4 text-xs text-gray-500 dark:text-[#8b949e] font-medium max-w-[280px] truncate">{e.details || '-'}</td>
                              <td className="p-4 font-black font-mono text-rose-600 dark:text-rose-400">-{e.amountLabel} د.ع</td>
                              <td className="p-4 text-center">
                                {props.canCancel && e.cancellable && (
                                  <button
                                    onClick={() => props.onCancelOperation(e.id)}
                                    className="p-2 text-gray-300 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-lg transition-all opacity-0 group-hover:opacity-100 cursor-pointer"
                                    title={t('pf.delete_expense')}
                                    data-testid="fin-expense-cancel"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                          {props.expenseRows.length === 0 && (
                            <tr>
                              <td colSpan={6} className="p-16 text-center text-gray-400 font-bold">
                                <TrendingDown size={40} className="mx-auto text-gray-200 dark:text-gray-800 mb-3 opacity-30" />
                                {t('pf.no_expenses')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
